/**
 * Enhanced error logging for API errors.
 *
 * This module provides utilities to log detailed error information to help
 * debug issues that manifest as CORS errors but are actually backend issues.
 */

export interface ApiErrorDetails {
  url: string;
  method: string;
  status?: number;
  statusText?: string;
  responseBody?: any;
  requestBody?: any;
  timestamp: string;
  userAgent: string;
}

/**
 * Log detailed API error information to console.
 *
 * This helps distinguish between actual CORS issues and backend errors
 * that appear as network errors in the browser.
 */
export function logApiError(error: any, url: string, method: string = 'GET', requestBody?: any) {
  const errorDetails: ApiErrorDetails = {
    url,
    method,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
  };

  // Extract error details
  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    errorDetails.status = error.response.status;
    errorDetails.statusText = error.response.statusText;
    errorDetails.responseBody = error.response.data;

    console.group(`🔴 API Error: ${method} ${url}`);
    console.error('Status:', errorDetails.status, errorDetails.statusText);
    console.error('Response:', errorDetails.responseBody);

    // Special handling for validation errors
    if (errorDetails.responseBody?.error === 'Response Serialization Error') {
      console.error('⚠️  VALIDATION ERROR DETECTED ⚠️');
      console.error('This is a backend data validation issue, not a CORS problem!');
      console.error('Details:', errorDetails.responseBody.details);
      console.error('Hint:', errorDetails.responseBody.hint);
    }

  } else if (error.request) {
    // The request was made but no response was received
    // This is where CORS issues or network errors appear
    console.group(`🔴 Network Error: ${method} ${url}`);
    console.error('No response received from server');
    console.error('This could be:');
    console.error('  1. CORS configuration issue');
    console.error('  2. Backend server is down');
    console.error('  3. Network connectivity issue');
    console.error('  4. Backend error before CORS headers are added');

  } else {
    // Something happened in setting up the request that triggered an Error
    console.group(`🔴 Request Setup Error: ${method} ${url}`);
    console.error('Error:', error.message);
  }

  if (requestBody) {
    errorDetails.requestBody = requestBody;
    console.error('Request Body:', requestBody);
  }

  console.error('Full Error Object:', error);
  console.error('Error Details:', errorDetails);
  console.groupEnd();

  // Store error in session storage for debugging
  try {
    const recentErrors = JSON.parse(sessionStorage.getItem('api_errors') || '[]');
    recentErrors.push(errorDetails);

    // Keep only last 50 errors
    if (recentErrors.length > 50) {
      recentErrors.shift();
    }

    sessionStorage.setItem('api_errors', JSON.stringify(recentErrors));
  } catch (e) {
    // Ignore storage errors
  }

  return errorDetails;
}

/**
 * Get recent API errors from session storage.
 */
export function getRecentApiErrors(): ApiErrorDetails[] {
  try {
    return JSON.parse(sessionStorage.getItem('api_errors') || '[]');
  } catch {
    return [];
  }
}

/**
 * Clear stored API errors.
 */
export function clearApiErrors() {
  sessionStorage.removeItem('api_errors');
}

/**
 * Check if an error is likely a CORS issue vs a backend issue.
 */
export function isCorsError(error: any): { isCors: boolean; reason: string } {
  // If we have a response, it's not a CORS issue
  if (error.response) {
    return {
      isCors: false,
      reason: `Server responded with status ${error.response.status}`
    };
  }

  // If we have no request, it's a client-side error
  if (!error.request) {
    return {
      isCors: false,
      reason: 'Request was never sent (client-side error)'
    };
  }

  // Network errors with no response could be CORS
  return {
    isCors: true,
    reason: 'No response received - could be CORS, network, or backend crash before CORS headers'
  };
}

/**
 * Enhanced error message for display to users.
 */
export function getUserFriendlyErrorMessage(error: any): string {
  if (error.response) {
    // We have a response from the server
    const status = error.response.status;
    const data = error.response.data;

    // Check for validation errors
    if (data?.error === 'Response Serialization Error') {
      return 'Server encountered a data validation error. This has been logged for investigation. Please try again or contact support.';
    }

    // Standard HTTP errors
    if (status === 400) {
      return data?.detail || 'Invalid request. Please check your input.';
    }
    if (status === 401) {
      return 'Your session has expired. Please log in again.';
    }
    if (status === 403) {
      return 'You do not have permission to perform this action.';
    }
    if (status === 404) {
      return 'The requested resource was not found.';
    }
    if (status === 422) {
      return data?.detail || 'Validation error. Please check your input.';
    }
    if (status >= 500) {
      return data?.detail || 'Server error. Please try again later.';
    }

    return data?.detail || `Request failed with status ${status}`;
  }

  // No response from server
  const corsCheck = isCorsError(error);
  if (corsCheck.isCors) {
    return 'Unable to connect to server. Please check your internet connection and try again.';
  }

  return error.message || 'An unexpected error occurred';
}
